import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "../../../utils/index.js";
import {
  Button,
  Chip,
  Icon,
  SearchBar,
  Select,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import type { Commentary } from "../../../api/commentaries.service.js";
import { DataFilters } from "../../../components/DataFilters/DataFilters.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { PageHeader } from "../../../components/PageHeader/PageHeader.js";
import { PaginationControls } from "../../../components/PaginationControls/index.js";
import PermissionGate from "../../../components/Permissions/PermissionGate.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { useCommentaries } from "../../../hooks/useCommentaries.js";
import { useFilters } from "../../../hooks/useFilters.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import { useUrlParams } from "../../../hooks/useUrlParams.js";
import { useMutation } from "../../../hooks/useApi.js";
import { commentaries as commentariesApi } from "../../../api/commentaries.service.js";
import { ROUTES } from "../../../routes.js";
import { formatDateTimeShort } from "../../../utils/dateFormatter.js";
import { TaxonomyColumn, type CommentaryTaxonomyValues } from "./components/TaxonomyColumn.js";
import { stripDirectoryPrefix } from "./commentaryForm.helpers.js";
import styles from "./Commentaries.module.css";
import { useLocation, useNavigate } from "react-router-dom";

type CommentaryListRow = {
  id: string;
  assignee: string;
  status: Commentary["status"];
  createdAt: string;
  updatedAt: string;
  taxonomy: CommentaryTaxonomyValues;
  commentary: Commentary;
};

function getTaxonomyValue(commentary: Commentary, refType: string): string {
  switch (refType) {
    case "PUBLICATION":
      return commentary.publicationName || commentary.publicationId || "-";
    case "EDITION": {
      const editionName = commentary.editionName || commentary.editionId || "-";
      if (editionName === "-") return editionName;
      const pubName = commentary.publicationName;
      return stripDirectoryPrefix(editionName, pubName);
    }
    case "LOCATION":
      return commentary.locationName || commentary.locationId || "-";
    case "BUSINESS_PRACTICE_AREA":
      return commentary.bpaName || commentary.bpaId || "-";
    case "FIRM":
      return commentary.firmName || commentary.firmId || "-";
    default:
      return "-";
  }
}

function formatCommentaryDate(value: string): string {
  if (!value) {
    return "-";
  }

  try {
    return formatDateTimeShort(value);
  } catch {
    return "-";
  }
}

function StatusChip({ status }: { status: Commentary["status"] }) {
  if (status === "DRAFT") {
    return (
      <Chip size="small" variant="filled" className={styles.statusBadgeDraft}>
        Draft
      </Chip>
    );
  }
  if (status === "TO_REVIEW") {
    return (
      <Chip size="small" variant="filled" className={styles.statusBadgeReview}>
        To Review
      </Chip>
    );
  }
  if (status === "READY_FOR_PRODUCT") {
    return (
      <Chip size="small" variant="filled" className={styles.statusBadgeReadyForProduct}>
        Ready for Product
      </Chip>
    );
  }
  return (
    <Chip size="small" variant="filled" className={styles.statusBadgeApproved}>
      Approved
    </Chip>
  );
}

function CommentariesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { searchParams, updateUrl } = useUrlParams();
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "all",
    assignee: searchParams.get("assignee") || "all",
  });
  const [searchValue, setSearchValue] = useState(searchParams.get("assignee") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 10);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    const state = location.state as { toast?: { message: string; type: ToastType } } | null;
    if (state?.toast) {
      setToast(state.toast);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  const { activeFilters, handleFilterChange, handleClearFilter } = useFilters({
    filters,
    setFilters,
    updateUrl,
    labelOverrides: {
      assignee: "Assignee Email",
      status: "Status",
    },
  });

  const statusFilter =
    filters.status === "all" ? undefined : (filters.status as Commentary["status"]);
  const assigneeFilter = filters.assignee === "all" ? undefined : filters.assignee;
  const canEditCommentary = hasPermission("staff.research.edit");
  const canChangeStatus = hasPermission("staff.research.change_status");
  const canApprove = hasPermission("staff.research.approve");

  const { mutate: updateStatus, loading: isUpdatingStatus } = useMutation(
    commentariesApi.updateStatus,
  );

  const { data, loading, error, refetch } = useCommentaries({
    limit: itemsPerPage,
    offset: (currentPage - 1) * itemsPerPage,
    status: statusFilter,
    assignee: assigneeFilter,
  });

  const rows = useMemo<CommentaryListRow[]>(() => {
    const commentaries = data?.commentaries || [];
    return commentaries.map(item => ({
      id: item.id,
      assignee: item.assignee || "-",
      status: item.status,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      commentary: item,
      taxonomy: {
        publication: getTaxonomyValue(item, "PUBLICATION"),
        edition: getTaxonomyValue(item, "EDITION"),
        region: getTaxonomyValue(item, "LOCATION"),
        practice: getTaxonomyValue(item, "BUSINESS_PRACTICE_AREA"),
        firm: getTaxonomyValue(item, "FIRM"),
      },
    }));
  }, [data?.commentaries]);

  const pagination = data?.pagination || {
    total: 0,
    limit: itemsPerPage,
    offset: 0,
    hasMore: false,
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / itemsPerPage));
  const startIndex = pagination.offset;
  const endIndex = pagination.offset + rows.length;

  const columns: Column<CommentaryListRow>[] = [
    {
      header: "Assignee",
      accessor: "assignee",
      render: value => String(value ?? "-"),
    },
    {
      header: "Status",
      accessor: "status",
      render: (value, row) => {
        const currentStatus = value as Commentary["status"];

        const currentStatusLabel =
          currentStatus === "TO_REVIEW"
            ? "To Review"
            : currentStatus === "DRAFT"
              ? "Draft"
              : currentStatus === "READY_FOR_PRODUCT"
                ? "Ready for Product"
                : "Approved";

        const options = [
          {
            value: currentStatus,
            label: currentStatusLabel,
          },
        ];

        if (currentStatus === "DRAFT" && canChangeStatus) {
          options.push({ value: "TO_REVIEW", label: "To Review" });
        } else if (currentStatus === "TO_REVIEW") {
          if (canChangeStatus) options.push({ value: "DRAFT", label: "Draft" });
          if (canApprove) options.push({ value: "APPROVED", label: "Approved" });
        } else if (currentStatus === "APPROVED" && canApprove) {
          options.push({ value: "DRAFT", label: "Draft" });
          options.push({ value: "READY_FOR_PRODUCT", label: "Ready for Product" });
        } else if (currentStatus === "READY_FOR_PRODUCT" && canApprove) {
          options.push({ value: "DRAFT", label: "Draft" });
        }

        if (options.length <= 1) {
          return (
            <div
              className={styles.statusNotAllowed}
              title="You don't have permission to change status"
            >
              <StatusChip status={currentStatus} />
            </div>
          );
        }

        return (
          <div onClick={e => e.stopPropagation()} title="Change status">
            <Select
              aria-label={`Change status for ${row.commentary.id}`}
              size="small"
              value={currentStatus}
              disabled={isUpdatingStatus}
              onChange={e => {
                const newStatus = e.target.value as Commentary["status"];
                updateStatus(row.id, newStatus)
                  .then(() => {
                    const label =
                      newStatus === "TO_REVIEW"
                        ? "Submitted for review"
                        : newStatus === "APPROVED"
                          ? "Commentary approved"
                          : newStatus === "READY_FOR_PRODUCT"
                            ? "Marked ready for product"
                            : "Returned to draft";
                    setToast({ message: label, type: "success" });
                    refetch();
                  })
                  .catch((err: unknown) => {
                    const errorMessage =
                      err instanceof Error ? err.message : "Failed to update status";
                    setToast({ message: errorMessage, type: "error" });
                  });
              }}
              options={options}
            />
          </div>
        );
      },
    },
    {
      header: "Created at",
      accessor: "createdAt",
      render: value => formatCommentaryDate(String(value ?? "")),
    },
    {
      header: "Updated At",
      accessor: "updatedAt",
      render: value => formatCommentaryDate(String(value ?? "")),
    },
    {
      header: "Taxonomy",
      accessor: "taxonomy",
      render: (_value, row) => <TaxonomyColumn taxonomy={row.taxonomy} />,
    },
  ];

  const isAuthError =
    error &&
    ((error as Error & { status?: number }).status === 401 ||
      (error as Error & { status?: number }).status === 403);

  const handleFiltersChange = (key: string, value: string | number | boolean) => {
    handleFilterChange(key, value, { page: "1" });
    setCurrentPage(1);
  };

  const handleFiltersChangeRef = useRef(handleFiltersChange);
  handleFiltersChangeRef.current = handleFiltersChange;

  const debouncedAssigneeChangeRef = useRef(
    debounce((value: unknown) => {
      handleFiltersChangeRef.current("assignee", (value as string) || "all");
    }, 300),
  );
  const debouncedAssigneeChange = debouncedAssigneeChangeRef.current;

  useEffect(() => {
    return () => debouncedAssigneeChangeRef.current.cancel();
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedAssigneeChange(value);
  };

  const handleSearchClear = () => {
    debouncedAssigneeChange.cancel();
    setSearchValue("");
    handleFiltersChange("assignee", "all");
  };

  const handleFiltersClear = (key: string) => {
    handleClearFilter(key, { page: "1" });
    setCurrentPage(1);
  };

  const renderTableContent = () => {
    if (loading) {
      return <LoadingState message="Loading commentaries..." />;
    }

    if (isAuthError) {
      return <UnauthorizedView />;
    }

    if (error) {
      return (
        <EmptyState
          variant="error"
          title="Failed to load commentaries"
          message={error.message}
          action={{
            label: "Retry",
            onClick: () => refetch(),
          }}
        />
      );
    }

    if (rows.length === 0) {
      return (
        <EmptyState
          variant="empty"
          title="No commentaries found"
          message="Try changing your filters and search criteria."
        />
      );
    }

    return (
      <>
        <div className={styles.tableContainer}>
          <DataTable
            data={rows}
            columns={columns}
            rowKey="id"
            onRowClick={
              canEditCommentary
                ? row => {
                    navigate(ROUTES.research.commentariesDetail(row.id), {
                      state: {
                        returnSearch: location.search,
                        prefetchedCommentary: row.commentary,
                      },
                    });
                  }
                : undefined
            }
          />
        </div>
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalCount={pagination.total}
          itemName="commentaries"
          onPageChange={page => {
            setCurrentPage(page);
            updateUrl({ page: String(page) });
          }}
        />
      </>
    );
  };

  return (
    <>
      <div className={styles.wrapper}>
        <PageHeader title="Research Assignment" subtitle="Research commentaries management">
          <PermissionGate requireAll={["staff.research.create", "taxonomy.read"]}>
            <Button
              onClick={() => navigate(ROUTES.research.createCommentary)}
              variant="contained"
              color="primary"
              startIcon={<Icon icon="plus" size="small" />}
              className={styles.createButton}
              aria-label="Create Commentary"
            >
              Create
            </Button>
          </PermissionGate>
        </PageHeader>

        <div className={styles.search}>
          <SearchBar
            value={searchValue}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            placeholder="Search assignee..."
          />
        </div>

        <div className={styles.filters}>
          <DataFilters
            filters={[
              {
                key: "status",
                label: "Status",
                options: [
                  { value: "all", label: "All statuses" },
                  { value: "DRAFT", label: "Draft" },
                  { value: "TO_REVIEW", label: "To Review" },
                  { value: "APPROVED", label: "Approved" },
                  { value: "READY_FOR_PRODUCT", label: "Ready for Product" },
                ],
              },
            ]}
            values={filters}
            onChange={handleFiltersChange}
            activeFilters={activeFilters}
            onClearFilter={handleFiltersClear}
          >
            <div className={styles.rowsPerPageControl}>
              <label htmlFor="commentariesRowsPerPage" className={styles.rowsPerPageLabel}>
                Rows per page:
              </label>
              <Select
                id="commentariesRowsPerPage"
                value={itemsPerPage}
                onChange={event => {
                  const pageSize = Number(event.target.value);
                  setItemsPerPage(pageSize);
                  setCurrentPage(1);
                  updateUrl({
                    pageSize: String(pageSize),
                    page: "1",
                  });
                }}
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
                size="small"
              />
            </div>
          </DataFilters>
        </div>

        {renderTableContent()}
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </>
  );
}

export default CommentariesPage;
